// Pure week-bucket helpers for the running progress surface (U7).
//
// All functions operate on the `CardioSessionSummary[]` shape returned by the
// U4 GET /api/fit/sessions endpoint. They bucket sessions by their week's
// Monday (Mon-first, see `mondayOf` in cardio-queries.ts).
//
// Conventions:
//   - `weekMonday` is a YYYY-MM-DD string. Caller passes `mondayOfToday()` for
//     "this week", or `shiftMondayWeeks(...)` outputs for historical windows.
//   - "Z2-prescribed run" = workout_type ∈ {Run-Easy, Run-Long}. Matches the
//     prescribed-zone mapping in apps/protocol-api/src/routes/fit.ts.
//   - Helpers tolerate null/missing fields silently (skip; never throw).

import { mondayOf, shiftMondayWeeks } from './cardio-queries';
import type { CardioSessionSummary } from './types';

/** A run whose prescribed zone is Z2 — the only ones for which the cached
 *  pct_in_prescribed_zone / pace_at_zone columns describe Z2 specifically. */
function isZ2Run(s: CardioSessionSummary): boolean {
  return s.workout_type === 'Run-Easy' || s.workout_type === 'Run-Long';
}

/** Returns true when `recorded_date` belongs to the week starting on
 *  `weekMonday`. Bad dates are dropped. */
function inWeek(s: CardioSessionSummary, weekMonday: string): boolean {
  try {
    return mondayOf(s.recorded_date) === weekMonday;
  } catch {
    return false;
  }
}

/** Sum of distance / duration for all runs in the week. */
export function weeklyVolume(
  sessions: CardioSessionSummary[],
  weekMonday: string,
): { km: number; minutes: number } {
  let meters = 0;
  let seconds = 0;
  for (const s of sessions) {
    if (s.kind !== 'run') continue;
    if (!inWeek(s, weekMonday)) continue;
    if (s.distance_m != null) meters += s.distance_m;
    if (s.duration_s != null) seconds += s.duration_s;
  }
  return { km: meters / 1000, minutes: seconds / 60 };
}

/** The single longest run in the week (by distance). Null when 0 runs. */
export function longestRun(
  sessions: CardioSessionSummary[],
  weekMonday: string,
): { km: number; minutes: number } | null {
  let best: CardioSessionSummary | null = null;
  for (const s of sessions) {
    if (s.kind !== 'run') continue;
    if (!inWeek(s, weekMonday)) continue;
    const d = s.distance_m ?? 0;
    const bd = best?.distance_m ?? -1;
    if (d > bd) best = s;
  }
  if (!best) return null;
  return {
    km: (best.distance_m ?? 0) / 1000,
    minutes: (best.duration_s ?? 0) / 60,
  };
}

/**
 * Approximate weekly time spent in Z2 (minutes).
 *
 * Per session, Z2 time ≈ `duration_s * (pct_in_prescribed_zone / 100)` — but
 * only for runs whose prescribed zone IS Z2 (Run-Easy / Run-Long today).
 * Runs that prescribe other zones (future Run-Tempo / intervals) don't
 * carry a directly-usable Z2 fraction in the cached summary, so we skip
 * them here. That's a deliberate undercount, not a bug.
 */
export function weeklyZ2Time(
  sessions: CardioSessionSummary[],
  weekMonday: string,
): number {
  let totalSeconds = 0;
  for (const s of sessions) {
    if (s.kind !== 'run') continue;
    if (!inWeek(s, weekMonday)) continue;
    if (!isZ2Run(s)) continue;
    if (s.duration_s == null || s.pct_in_prescribed_zone == null) continue;
    totalSeconds += s.duration_s * (s.pct_in_prescribed_zone / 100);
  }
  return totalSeconds / 60;
}

/**
 * Count Z2 runs at or after `sinceWeekMonday`. Used to gate the
 * "INSUFFICIENT DATA · NEED ≥3 Z2 RUNS" state on the pace-at-Z2 hero — a
 * trend isn't meaningful until the operator has 3+ in-zone runs in the
 * window.
 */
export function countZ2Runs(
  sessions: CardioSessionSummary[],
  sinceWeekMonday: string,
): number {
  let n = 0;
  for (const s of sessions) {
    if (s.kind !== 'run') continue;
    if (!isZ2Run(s)) continue;
    let wm: string;
    try {
      wm = mondayOf(s.recorded_date);
    } catch {
      continue;
    }
    if (wm >= sinceWeekMonday) n += 1;
  }
  return n;
}

export type PaceAtZ2Point = {
  /** YYYY-MM-DD of the week's Monday. */
  weekMonday: string;
  /** Duration-weighted average pace-at-Z2 across Z2 runs in the week.
   *  Null when no Z2 runs / no usable pace_at_zone rows. */
  paceSPerKm: number | null;
  /** Count of Z2 runs that contributed (regardless of pace_at_zone presence). */
  runCount: number;
};

/**
 * Pace-at-Z2 trend across the last `weeks` weeks, oldest first. The series
 * always has length `weeks`; weeks with no Z2 runs surface as
 * `paceSPerKm: null`. Caller is free to filter nulls before charting.
 *
 * Weighting: each Z2 run with a non-null pace_at_zone_s_per_km contributes
 * `pace * duration_s` to a numerator and `duration_s` to a denominator
 * (so longer runs anchor the week's average).
 */
export function paceAtZ2Trend(
  sessions: CardioSessionSummary[],
  weeks: number,
): PaceAtZ2Point[] {
  // Build the ordered list of Monday-ymds from oldest to newest.
  const out: PaceAtZ2Point[] = [];
  // Pre-bucket Z2 runs by their week-monday so we don't N×W scan.
  const byWeek = new Map<
    string,
    { num: number; den: number; runCount: number }
  >();
  for (const s of sessions) {
    if (s.kind !== 'run') continue;
    if (!isZ2Run(s)) continue;
    let wm: string;
    try {
      wm = mondayOf(s.recorded_date);
    } catch {
      continue;
    }
    let bucket = byWeek.get(wm);
    if (!bucket) {
      bucket = { num: 0, den: 0, runCount: 0 };
      byWeek.set(wm, bucket);
    }
    bucket.runCount += 1;
    if (s.pace_at_zone_s_per_km != null && s.duration_s != null && s.duration_s > 0) {
      bucket.num += s.pace_at_zone_s_per_km * s.duration_s;
      bucket.den += s.duration_s;
    }
  }
  // Walk from (weeks-1) weeks ago up to this week.
  const today = new Date();
  const yy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  const thisMon = mondayOf(`${yy}-${mm}-${dd}`);
  for (let i = weeks - 1; i >= 0; i -= 1) {
    const wm = shiftMondayWeeks(thisMon, -i);
    const bucket = byWeek.get(wm);
    const pace = bucket && bucket.den > 0 ? bucket.num / bucket.den : null;
    out.push({
      weekMonday: wm,
      paceSPerKm: pace,
      runCount: bucket?.runCount ?? 0,
    });
  }
  return out;
}

